// SPDX-License-Identifier: GPL-3.0-or-later
import type { MaterializationRequest, MaterializedPages } from "./multikey-materializer";
import type { MultikeyOccurrence } from "./multikey";

/** Version of the stable INIT transport envelope, independent of the processing policy. */
export const MULTIKEY_WORKER_PROTOCOL_VERSION = 2;
/** Parent-only, versioned processing policy. This module has no engine or DOM runtime imports. */
export const MULTIKEY_MATERIALIZER_PROFILE = "ulsbs-range-canonical-names-v1/pdf-lib-1.17.1";
export const MULTIKEY_MAX_INPUT_BYTES = 256 * 1024 * 1024;
export const MULTIKEY_MAX_JOB_PAGES = 16;
export const MULTIKEY_CACHE_BYTES = 32 * 1024 * 1024;

/** PDF.js-compatible visible page geometry, in unscaled PDF user space. */
export interface MultikeyPageGeometry {
  readonly view: readonly [number, number, number, number];
  readonly rotation: number;
  readonly userUnit: number;
}

/** Plain structured-cloneable inspection result; identity hashes the exact original bytes. */
export interface MultikeySourceInfo {
  readonly pageCount: number;
  readonly geometry: readonly MultikeyPageGeometry[];
  readonly layerNames: readonly string[];
  readonly producer?: string;
  readonly creator?: string;
  readonly documentVersion?: string;
  readonly identity: string;
}

/** Index-owned metadata; geometry is local to this range, occurrences remain global. */
export interface MultikeyRangeMetadata {
  readonly identity: string;
  readonly byteLength: number;
  readonly geometry: readonly MultikeyPageGeometry[];
  readonly firstPage: number;
  readonly occurrences: readonly MultikeyOccurrence[];
}

/** Initialization checks code compatibility without loading any PDF. */
export type MultikeyWorkerRequest = { readonly id: number; readonly generation: number } & (
  | { readonly type: "init" }
  | {
      readonly type: "convert";
      readonly data: ArrayBuffer;
      readonly range: MultikeyRangeMetadata;
      readonly request: MaterializationRequest;
    }
);

/** Each reply is tied to one request and one physical worker lifetime. */
export type MultikeyWorkerReply = { readonly id: number; readonly generation: number } & (
  | { readonly ok: false; readonly error: string }
  | {
      readonly ok: true;
      readonly type: "init";
      /** Exact transport version compiled into this physical worker. */
      readonly protocolVersion: number;
      /** Exact byte-processing policy compiled into this physical worker. */
      readonly profile: string;
    }
  | { readonly ok: true; readonly type: "convert"; readonly result: MaterializedPages }
);
